import * as React from 'react';
import Media from 'react-media';

import classNames from 'classnames';

import type UserAgentInfo from 'js/lib/useragent';

import { Link, Typography2 } from '@coursera/cds-core';
import { ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

type BreadcrumbItem = {
  href?: string | null;
  title: string;
};

type BreadcrumbsProps = {
  breadcrumbItems: Array<BreadcrumbItem>;
};

export const DesktopBreadcrumbs = ({ breadcrumbItems }: BreadcrumbsProps) => {
  return (
    <ul data-unit="breadcrumbs-list" className="rc-EntityPageBreadcrumbs">
      {breadcrumbItems.map((item, index) => {
        return (
          <li className="breadcrumb-item" key={`breadcrumb-${item.title}`}>
            {item.href ? (
              <Link
                typographyVariant="bodyPrimary"
                variant="quiet"
                className={classNames('breadcrumb-title', 'is-link')}
                href={item.href}
              >
                {item.title}
              </Link>
            ) : (
              <Typography2 component="span" className="breadcrumb-title">
                {item.title}
              </Typography2>
            )}
            {index !== breadcrumbItems.length - 1 && (
              <ChevronNextIcon color="default" size="small" className="breadcrumb-divider" />
            )}
          </li>
        );
      })}
    </ul>
  );
};

type MobileBreadcrumbsProps = {
  href?: string | null;
  label: string;
};
export const MobileBreadcrumb = ({ href, label }: MobileBreadcrumbsProps) => {
  if (!label || !href) {
    return null;
  }

  return (
    <ul data-unit="mobile-breadcrumb" className="rc-EntityPageBreadcrumbs mobile">
      <li className="breadcrumb-item" key="breadcrumb-divider">
        <Link
          typographyVariant="bodyPrimary"
          variant="quiet"
          style={{ marginRight: '0px' }}
          className={classNames('breadcrumb-title', 'breadcrumb-item', 'is-link')}
          href={href}
        >
          <ChevronPreviousIcon color="interactive" size="small" className="breadcrumb-divider" />
        </Link>
      </li>
      <li className="breadcrumb-item" key={`breadcrumb-${label}`}>
        <Link
          typographyVariant="bodyPrimary"
          variant="quiet"
          className={classNames('breadcrumb-title', 'is-link')}
          href={href}
        >
          {label}
        </Link>
      </li>
    </ul>
  );
};

type EntityPageBreadcrumbsProps = {
  userAgent?: UserAgentInfo;
  breadcrumbItems: Array<BreadcrumbItem>;
};

export const EntityPageBreadcrumbs = ({ breadcrumbItems, userAgent }: EntityPageBreadcrumbsProps) => {
  if (!breadcrumbItems || !breadcrumbItems.length) {
    return null;
  }

  return (
    <Media query={{ maxWidth: 1024 }} defaultMatches={userAgent && userAgent.isMobileBrowser}>
      {(matches) => {
        if (matches) {
          return <MobileBreadcrumb href={breadcrumbItems[0].href} label={breadcrumbItems[0].title} />;
        }

        return <DesktopBreadcrumbs breadcrumbItems={breadcrumbItems} />;
      }}
    </Media>
  );
};

export default EntityPageBreadcrumbs;
